import { useState, useEffect } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { fetchLegalPageBySlug } from "./supabase";
import { useTheme } from "./ThemeInjector";
import { useLanguage } from "./LanguageContext";

/** Simple markdown-like to HTML for legal content (paragraphs, headers, bold, links) */
function simpleMarkdownToHtml(text: string): string {
  return text
    .replace(/\n\n+/g, "</p><p>")
    .replace(/\n/g, "<br/>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\[([^\]]+)\]\((mailto:[^)]+)\)/g, '<a href="$2">$1</a>')
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^(.+)$/gm, (line) => (line.startsWith("<") ? line : "<p>" + line + "</p>"));
}

export function LegalPage() {
  const { slug } = useParams<{ slug: string }>();
  const theme = useTheme();
  const { t } = useLanguage();
  const [page, setPage] = useState<{ title: string; summary?: string | null; content: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setNotFound(false);
    fetchLegalPageBySlug(slug)
      .then(({ data, error }) => {
        setLoading(false);
        if (error || !data) {
          setNotFound(true);
          return;
        }
        setPage({
          title: data.title ?? "",
          summary: data.summary ?? null,
          content: data.content ?? "",
        });
      })
      .catch(() => {
        setLoading(false);
        setNotFound(true);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-28 pb-24 px-6 max-w-3xl mx-auto text-center">
        <p style={{ fontFamily: theme.fontBody, fontSize: "15px", color: theme.muted }}>Loading…</p>
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="pt-28 pb-24 px-6 max-w-2xl mx-auto text-center">
        <p className="tracking-[0.2em] uppercase mb-4" style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}>
          Not found
        </p>
        <h1 className="mb-6" style={{ fontFamily: theme.fontHeading, fontSize: "28px", fontWeight: 400, color: theme.fg }}>
          Page not found
        </h1>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-6 py-3 tracking-[0.2em] uppercase border transition-colors"
          style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.primary, borderColor: theme.primary, borderRadius: theme.btnRadius }}
        >
          <ArrowLeft size={14} /> Home
        </Link>
      </div>
    );
  }

  const htmlContent = simpleMarkdownToHtml(page.content);

  return (
    <div className="pt-28 pb-24 px-6">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 mb-10 tracking-[0.15em] uppercase transition-opacity hover:opacity-80"
          style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.muted }}
        >
          <ArrowLeft size={14} /> Home
        </Link>
        <h1
          className="tracking-[0.06em] mb-4"
          style={{ fontFamily: theme.fontHeading, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 400, color: theme.fg }}
        >
          {page.title}
        </h1>
        {page.summary && (
          <p
            className="leading-[1.8] mb-10"
            style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}
          >
            {page.summary}
          </p>
        )}
        <div
          className="legal-content max-w-none"
          style={{
            fontFamily: theme.fontBody,
            fontSize: "14px",
            fontWeight: 300,
            color: theme.fg,
            lineHeight: 1.8,
            "--policy-link": theme.primary,
          } as React.CSSProperties}
        >
          <div
            dangerouslySetInnerHTML={{ __html: htmlContent }}
            className="[&>p]:mb-4 [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:text-[1rem] [&>h2]:font-normal [&>h2]:tracking-[0.02em] [&>h3]:mt-6 [&>h3]:mb-2 [&>h3]:text-[14px] [&>h3]:font-normal [&>strong]:font-medium [&_a]:text-[var(--policy-link)] [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:opacity-80"
          />
        </div>
      </div>
    </div>
  );
}
